import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';

function RecentThreadsView() {
  const [threads, setThreads] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastPostPages, setLastPostPages] = useState({});

  // Helper function to calculate page number for a post
  const calculatePostPage = async (threadId, postId) => {
    try {
      const response = await axiosInstance.get(`/posts/thread/${threadId}/post/${postId}`);
      const postsPerPage = 20; // Should match backend limit
      const postIndex = response.data.index;
      return Math.ceil((postIndex + 1) / postsPerPage);
    } catch (error) {
      console.error('Error calculating post page:', error);
      return 1; // Fallback to page 1
    }
  };

  const fetchRecentThreads = async (pageToLoad = 1) => {
    try {
      if (pageToLoad === 1) setLoading(true);
      else setLoadingMore(true);

      const res = await axiosInstance.get(`/thread-activity/recent?page=${pageToLoad}`);
      const newThreads = res.data.threads;
      const more = res.data.hasMore;

      // Combine threads with existing ones, then sort the entire list
      const combinedThreads = pageToLoad === 1 ? newThreads : [...threads, ...newThreads];
      
      // Sort all threads by last post date (newest first)
      // If a thread has no lastPost, fall back to thread creation date
      // This ensures the most recently active threads appear at the top
      const sortedThreads = combinedThreads.sort((a, b) => {
        const aDate = a.lastPost?.createdAt || a.createdAt;
        const bDate = b.lastPost?.createdAt || b.createdAt;
        return new Date(bDate) - new Date(aDate);
      });

      setThreads(sortedThreads);
      setHasMore(more);
      
      // Calculate pages for last posts - optimized with Promise.all
      const pagesInfo = { ...lastPostPages };
      const pageCalculations = [];
      
      // Collect all page calculations for new threads
      for (const thread of newThreads) {
        if (thread.lastPost && !pagesInfo[thread._id]) {
          pageCalculations.push({
            threadId: thread._id,
            postId: thread.lastPost._id,
            promise: calculatePostPage(thread._id, thread.lastPost._id)
          });
        }
      }
      
      // Execute all calculations in parallel
      if (pageCalculations.length > 0) {
        const results = await Promise.allSettled(
          pageCalculations.map(calc => calc.promise)
        );
        
        // Process results
        pageCalculations.forEach((calc, index) => {
          const result = results[index];
          const page = result.status === 'fulfilled' ? result.value : 1;
          
          pagesInfo[calc.threadId] = {
            threadId: calc.threadId,
            postId: calc.postId,
            page: page,
          };
        });
        
        setLastPostPages(pagesInfo);
      }
      
      setLoading(false);
      setLoadingMore(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchRecentThreads(1);
  }, []);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchRecentThreads(nextPage);
  };

  const getLastPostUrl = (thread) => {
    const info = lastPostPages[thread._id];
    if (!info) return `/thread/${thread._id}#lastpost`;
    return `/thread/${info.threadId}?page=${info.page}#post-${info.postId}`;
  };

  const truncate = (str, length) => 
    str.length > length ? str.substring(0, length) + '...' : str;

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleString('de-DE', {
      dateStyle: 'short',
      timeStyle: 'short',
    });

  return (
    <div className="max-w-6xl mx-auto px-2 sm:px-4">
      <div className="text-center mb-12">
        <h1 className="text-5xl sm:text-6xl text-white font-marker mb-4 animate-fade-in">
          Neue Threads
        </h1>
        <p className="text-xl text-gray-300 font-light">
          Letzte 48 Stunden
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center mt-12">
          <div className="modern-card p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <span className="ml-3 text-lg text-gray-600 dark:text-gray-300">Lade Threads...</span>
            </div>
          </div>
        </div>
      ) : threads.length === 0 ? (
        <div className="modern-card p-8 text-center">
          <div className="text-gray-500 dark:text-gray-400 text-lg">
            Keine neuen Threads in den letzten 48 Stunden gefunden.
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {threads.map((thread, index) => (
            <div
              key={thread._id}
              className="modern-card group hover:scale-[1.02] transition-all duration-300"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
                  <div className="flex-1">
                    <Link
                      to={`/thread/${thread._id}`}
                      className="font-semibold text-xl hover:text-primary transition-colors duration-200 text-gray-800 dark:text-white group-hover:text-primary block mb-2"
                    >
                      {truncate(thread.title, 80)}
                    </Link>
                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        von {thread.author?.username || 'Unbekannt'}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        {formatDate(thread.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right min-w-[200px]">
                    <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
                      Letzter Beitrag:
                    </div>
                    {thread.lastPost ? (
                      <Link
                        to={getLastPostUrl(thread)}
                        className="hover:text-primary transition-colors duration-200 block"
                      >
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          von {thread.lastPost.author?.username || '—'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {formatDate(thread.lastPost.createdAt)}
                        </div>
                        <div className="text-xs text-primary font-medium mt-1">
                          Zum letzten Beitrag →
                        </div>
                      </Link>
                    ) : (
                      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                        Noch keine Antworten
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="modern-btn-primary px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Lade weitere...
                  </span>
                ) : (
                  'Weitere laden'
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RecentThreadsView;
